import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { callSheetPrintGaps, resolveLook } from '@flashpoint/site-dna';
import type { LeadRecord } from '@flashpoint/site-dna';
import { relativeTime, shortDate } from '../lib/dateFmt';
import { deleteLead } from '../lib/firestore';
import { hostnameToUrl } from '../routes/lead/PreviewLink';
import { STATUS_META } from '../lib/statusMeta';

function daysSince(iso: string | undefined | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

export function LeadListRow({ lead }: { lead: LeadRecord }) {
  const name = lead.siteDna.businessInfo.name?.trim() || '(untitled)';
  const industry = lead.context.industryVertical || lead.siteDna.businessInfo.industryVertical;
  const look = resolveLook(
    industry,
    lead.siteDna.designTokens.personality ?? [],
    lead.siteDna.designTokens.lookVariant ?? 0,
    lead.siteDna.designTokens.density,
  );
  const url = hostnameToUrl(lead.provisioning.hostname);
  const hostLabel = url ? url.replace(/^https:\/\//, '') : null;
  const meta = STATUS_META[lead.leadStatus];
  const inPipeline = lead.leadStatus === 'In Pipeline';

  return (
    <div
      className={`fp-row fp-lift relative flex h-full min-h-[9.5rem] flex-col gap-3 ${inPipeline ? 'opacity-80 hover:opacity-100' : ''}`}
      style={{ boxShadow: `inset 3px 0 0 ${meta.color}` }}
    >
      <Link to={`/leads/${lead.id}`} className="absolute inset-0 rounded-[12px]" aria-label={name} />
      <div className="pointer-events-none relative flex items-start justify-between gap-2">
        <h3 className="min-w-0 truncate font-display text-lg font-semibold text-nm-ink">{name}</h3>
        <div className="flex shrink-0 items-start gap-1.5">
          <span
            className="rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wide"
            style={{ background: meta.bg, color: meta.color }}
          >
            {lead.leadStatus}
          </span>
          <DeleteLeadControl lead={lead} />
        </div>
      </div>
      <p className="pointer-events-none relative min-h-[1.25rem] truncate text-sm text-nm-muted">
        {[industry, look.label].filter(Boolean).join(' · ') || '—'}
      </p>
      <div className="pointer-events-none relative mt-auto flex flex-col gap-1">
        <StageLine lead={lead} url={url} hostLabel={hostLabel} />
        <span className="text-[11px] text-nm-tertiary">{relativeTime(lead.updatedAt)}</span>
      </div>
    </div>
  );
}

/** The line that changes per stage: gaps for Discovery, preview for demos, repo for pipeline. */
function StageLine({
  lead,
  url,
  hostLabel,
}: {
  lead: LeadRecord;
  url: string | null;
  hostLabel: string | null;
}) {
  if (lead.leadStatus === 'Discovery') {
    const industry = lead.context.industryVertical || lead.siteDna.businessInfo.industryVertical || '';
    const gaps = callSheetPrintGaps(lead.siteDna.businessInfo, industry).length;
    return (
      <span className="flex items-center gap-2">
        <span
          className="rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide"
          style={
            gaps > 0
              ? { background: 'var(--nm-warn-bg)', color: 'var(--nm-warn)' }
              : { background: 'var(--nm-success-bg)', color: 'var(--nm-success)' }
          }
        >
          {gaps > 0 ? `${gaps} gap${gaps === 1 ? '' : 's'}` : 'Ready'}
        </span>
        <span className="truncate text-xs text-nm-tertiary">
          {gaps > 0 ? 'on the call sheet' : 'generate the preview'}
        </span>
      </span>
    );
  }

  if (lead.leadStatus === 'Demo Generated') {
    const days = daysSince(lead.provisioning.finishedAt ?? lead.updatedAt);
    return (
      <span className="flex min-w-0 items-baseline gap-2">
        {hostLabel && url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="pointer-events-auto min-w-0 truncate font-mono text-[11px] text-nm-accent hover:underline"
          >
            {hostLabel} ↗
          </a>
        ) : (
          <span className="truncate text-xs text-nm-tertiary">
            {lead.provisioning.status === 'idle' ? 'No preview' : lead.provisioning.status}
          </span>
        )}
        {days !== null && (
          <span className="whitespace-nowrap text-[11px] text-nm-tertiary">
            live {days === 0 ? 'today' : `${days}d`}
          </span>
        )}
      </span>
    );
  }

  const repo = lead.provisioning.repo?.trim();
  return (
    <span className="flex min-w-0 items-baseline gap-2">
      <span className="whitespace-nowrap text-xs text-nm-tertiary">
        Locked {shortDate(lead.provisioning.codeLockedAt)}
      </span>
      {repo && (
        <a
          href={`https://github.com/${repo}`}
          target="_blank"
          rel="noreferrer"
          className="pointer-events-auto min-w-0 truncate font-mono text-[11px] text-nm-muted hover:text-nm-accent hover:underline"
        >
          {repo}
        </a>
      )}
    </span>
  );
}

function DeleteLeadControl({ lead }: { lead: LeadRecord }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const repo = lead.provisioning.repo?.trim();
  const pagesProject = lead.provisioning.pagesProject?.trim();
  const hostname = lead.provisioning.hostname?.trim();
  const leftover = Boolean(repo || pagesProject || hostname);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) setOpen(false);
    };
    const onPointer = (e: MouseEvent) => {
      if (busy) return;
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open, busy]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteLead(lead.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div ref={rootRef} className="pointer-events-auto relative z-10">
      <button
        type="button"
        aria-label="Remove from Flashpoint"
        aria-expanded={open}
        aria-haspopup="dialog"
        disabled={busy}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setError(null);
          setOpen((v) => !v);
        }}
        className="rounded-md p-1 text-nm-tertiary hover:bg-nm-danger-bg hover:text-nm-danger disabled:opacity-50"
      >
        <TrashIcon />
      </button>
      {open && (
        <div
          role="dialog"
          aria-labelledby={titleId}
          aria-modal="false"
          className="fp-card absolute right-0 top-full z-20 mt-1 w-64 !p-3 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <p id={titleId} className="font-display text-sm font-semibold text-nm-ink">
            Remove from Flashpoint
          </p>
          <p className="mt-1.5 text-xs text-nm-muted">
            Wipes this call sheet, history, and uploaded assets in Firebase. Does not delete GitHub or Cloudflare.
          </p>
          {leftover && (
            <ul className="mt-2 space-y-0.5 font-mono text-[10px] text-nm-tertiary">
              {repo ? <li>Repo still live: {repo}</li> : null}
              {pagesProject ? <li>Pages still live: {pagesProject}</li> : null}
              {hostname ? <li>Host still live: {hostname}</li> : null}
            </ul>
          )}
          {error && (
            <p className="mt-2 text-xs text-nm-danger" role="alert">
              {error}
            </p>
          )}
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => setOpen(false)}
              className="fp-btn fp-btn-ghost !px-3 !py-1.5"
            >
              Cancel
            </button>
            <button type="button" disabled={busy} onClick={() => void confirm()} className="fp-btn fp-btn-success !px-3 !py-1.5">
              {busy ? 'Removing…' : 'Confirm'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M3.5 4.5h9M6.5 4.5V3h3v1.5M5 4.5l.5 8.5h5L11 4.5"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
